import { lazy, Suspense, type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import AdminLayout from '@/components/AdminLayout';
import ProtectedRoute from '@/components/ProtectedRoute';
// Entrée/auth chargées d'emblée (premier rendu rapide, petit chunk).
import LoginPage from '@/pages/LoginPage';
import ForbiddenPage from '@/pages/ForbiddenPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';

// Pages applicatives chargées à la demande (code-splitting) : le bundle initial
// (login) reste léger et résilient sur connexion lente — les libs lourdes
// (recharts du dashboard, leaflet de la carte) ne se téléchargent qu'au besoin.
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const PersonnelListPage = lazy(() => import('@/pages/personnel/ListPage'));
const PersonnelNewPage = lazy(() => import('@/pages/personnel/NewPage'));
const PersonnelDetailPage = lazy(() => import('@/pages/personnel/DetailPage'));
const ClientsListPage = lazy(() => import('@/pages/clients/ListPage'));
const ClientNewPage = lazy(() => import('@/pages/clients/NewPage'));
const ClientDetailPage = lazy(() => import('@/pages/clients/DetailPage'));
const DemandesListPage = lazy(() => import('@/pages/demandes/ListPage'));
const DevisListPage = lazy(() => import('@/pages/devis/ListPage'));
const DevisNewPage = lazy(() => import('@/pages/devis/NewPage'));
const ContratsListPage = lazy(() => import('@/pages/contrats/ListPage'));
const ContratDetailPage = lazy(() => import('@/pages/contrats/DetailPage'));
const ContratsASignerPage = lazy(() => import('@/pages/contrats/ASignerPage'));
const TarifsListPage = lazy(() => import('@/pages/tarifs/ListPage'));
const ConfigListPage = lazy(() => import('@/pages/config/ListPage'));
const UtilisateursPage = lazy(() => import('@/pages/config/UtilisateursPage'));
const RemplacementsListPage = lazy(() => import('@/pages/remplacements/ListPage'));
const MissionsListPage = lazy(() => import('@/pages/missions/ListPage'));
const FacturesListPage = lazy(() => import('@/pages/factures/ListPage'));
const FacturesAValiderPage = lazy(() => import('@/pages/factures/AValiderPage'));
const PaiementsAValiderPage = lazy(() => import('@/pages/factures/PaiementsAValiderPage'));
const RetardsPage = lazy(() => import('@/pages/factures/RetardsPage'));
const PaieListPage = lazy(() => import('@/pages/paie/ListPage'));
const PointagesListPage = lazy(() => import('@/pages/pointages/ListPage'));
const SignalementsListPage = lazy(() => import('@/pages/signalements/ListPage'));
const CarteListPage = lazy(() => import('@/pages/carte/ListPage'));
const AssuranceListPage = lazy(() => import('@/pages/assurance/ListPage'));
const CnpsListPage = lazy(() => import('@/pages/cnps/ListPage'));
const QualiteListPage = lazy(() => import('@/pages/qualite/ListPage'));
const EvaluationsListPage = lazy(() => import('@/pages/evaluations/ListPage'));
const FinanceParametresPage = lazy(() => import('@/pages/finance/ParametresPage'));
const ServicesPage = lazy(() => import('@/pages/finance/ServicesPage'));
const DemandesServicePage = lazy(() => import('@/pages/services/DemandesServicePage'));
const MessagingAuditPage = lazy(() => import('@/pages/messaging/AuditPage'));
const SupportListPage = lazy(() => import('@/pages/support/ListPage'));

const ADMIN_ROLES = ['ADMIN', 'RH', 'CONSEILLER'] as const;

function RouteFallback(): ReactElement {
  return (
    <div role="status" aria-live="polite" className="grid min-h-[40vh] place-items-center">
      <span className="flex items-center gap-3 font-sans text-sm text-sub">
        <span
          aria-hidden
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gold-500 border-t-transparent"
        />
        Chargement…
      </span>
    </div>
  );
}

export function AppRoutes(): ReactElement {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot" element={<ForgotPasswordPage />} />
        <Route path="/forbidden" element={<ForbiddenPage />} />
        <Route
          element={
            <ProtectedRoute requiredRoles={ADMIN_ROLES}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/personnel" element={<PersonnelListPage />} />
          <Route path="/personnel/new" element={<PersonnelNewPage />} />
          <Route path="/personnel/:id" element={<PersonnelDetailPage />} />
          <Route path="/clients" element={<ClientsListPage />} />
          <Route path="/clients/new" element={<ClientNewPage />} />
          <Route path="/clients/:id" element={<ClientDetailPage />} />
          <Route path="/demandes" element={<DemandesListPage />} />
          <Route path="/devis" element={<DevisListPage />} />
          <Route path="/devis/new" element={<DevisNewPage />} />
          <Route path="/contrats" element={<ContratsListPage />} />
          <Route path="/contrats/a-signer" element={<ContratsASignerPage />} />
          <Route path="/contrats/:id" element={<ContratDetailPage />} />
          <Route path="/tarifs" element={<TarifsListPage />} />
          <Route path="/remplacements" element={<RemplacementsListPage />} />
          <Route path="/missions" element={<MissionsListPage />} />
          <Route path="/factures" element={<FacturesListPage />} />
          <Route
            path="/factures/a-valider"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'RH']}>
                <FacturesAValiderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/factures/paiements"
            element={
              <ProtectedRoute requiredRoles={['ADMIN', 'RH']}>
                <PaiementsAValiderPage />
              </ProtectedRoute>
            }
          />
          <Route path="/factures/retards" element={<RetardsPage />} />
          <Route path="/paie" element={<PaieListPage />} />
          <Route path="/pointages" element={<PointagesListPage />} />
          <Route path="/signalements" element={<SignalementsListPage />} />
          <Route path="/carte" element={<CarteListPage />} />
          <Route path="/assurance" element={<AssuranceListPage />} />
          <Route path="/cnps" element={<CnpsListPage />} />
          <Route path="/qualite" element={<QualiteListPage />} />
          <Route path="/evaluations" element={<EvaluationsListPage />} />
          <Route
            path="/config"
            element={
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <ConfigListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/config/utilisateurs"
            element={
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <UtilisateursPage />
              </ProtectedRoute>
            }
          />
          <Route path="/finance" element={<FinanceParametresPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/demandes" element={<DemandesServicePage />} />
          <Route path="/messagerie" element={<MessagingAuditPage />} />
          <Route path="/support" element={<SupportListPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}
